<script setup lang="ts">
import { sortBy } from "lodash-es";
import type { ComputedRef } from "vue";
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSearch from "@/components/ui/MenuList/MenuListSearch.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Icon } from "@/model/icon";
import type { Art, Team } from "@/model/session";
import { useArtStore } from "@/store/art";
import { isNotUserTeam, useTeamStore } from "@/store/team";

const { t } = useI18n();

const props = defineProps<{
  selected?: Team;
  title?: string;
  showCurrentTeam?: boolean;
  showClear?: boolean;
  showOtherArts?: boolean;
  showExternal?: boolean;
}>();

const emit = defineEmits<{ select: [Team | null] }>();

const showingArts = ref(false);
const selectedArt = ref(useArtStore().current);
const searchVal = ref("");
const artListItemRefs = useTemplateRef("artListItems");
const otherArtsButtonRef = useTemplateRef("otherArtsButton");

// Teams in the current ART (potentially excluding the current team)
const teamsInCurrentArt: ComputedRef<Team[]> = computed(() => {
  const currentTeam = useTeamStore().current;
  // Show external teams only for current ART. External teams can exist in multiple ARTs,
  // mirroring to them would be ambiguous as we only have dependent_team_id but not dependent_team_art_id
  const withExternals = props.showExternal && selectedArt.value.id === useArtStore().current.id;
  return sortBy(
    useTeamStore()
      .teamsInArt(selectedArt.value.id, withExternals)
      .filter((team) => props.showCurrentTeam || team !== currentTeam),
    isNotUserTeam,
  );
});

// Teams to show (current list, filtered by the current search if applicable)
const teamsList = computed(() => filterBySearch(teamsInCurrentArt.value));

// All ARTs
const arts: ComputedRef<Art[]> = computed(() => useArtStore().arts || []);

// ARTs to show (filtered by current search if applicable)
const artsList = computed(() => filterBySearch(arts.value));

// Show the 'Other ARTs' button when there is >1 ART
const showArtNav = computed(() => props.showOtherArts && arts.value.length > 1);

// Search input placeholder depends on the context (Teams/ARTs)
const searchPlaceholder = computed(() =>
  showArtNav.value && showingArts.value ? t("action.searchArts") : t("action.searchTeams"),
);

/**
 * Emit the selected team
 */
const selectTeam = (team: Team | null) => emit("select", team);

/**
 * Show teams for the selected ART
 */
const selectArt = async (art: Art) => {
  selectedArt.value = art;
  showingArts.value = false;
  searchVal.value = "";

  await nextTick();
  otherArtsButtonRef.value?.focus();
};

/**
 * Show all ARTs the user can choose from
 */
const showArts = async () => {
  showingArts.value = true;
  searchVal.value = "";

  await nextTick();
  artListItemRefs.value?.[0]?.focus();
};

/**
 * Filter the given list by the search input (partial match on 'name' property)
 * @param list List of Teams or ARTs
 */
function filterBySearch<T extends Team | Art>(list: T[]): T[] {
  const input = searchVal.value?.toLowerCase();
  return input ? list.filter((item) => item.name.toLowerCase().includes(input)) : list;
}

function teamIcon(team: Team): Icon {
  return team.external ? "persona/external-team" : "persona/team";
}
</script>

<template>
  <MenuList selectable>
    <MenuListHeader>
      <MenuListTitle>{{ title || $t("action.team") }}</MenuListTitle>
      <MenuListSearch
        :key="String(showingArts)"
        :placeholder="searchPlaceholder"
        :icon-title="$t('label.teamSelector.searchTitle')"
        :results-count="showingArts ? artsList?.length : teamsList?.length"
        @search="searchVal = $event"
      />
    </MenuListHeader>
    <MenuListItems>
      <template v-if="!showingArts">
        <MenuListItem v-if="showArtNav" ref="otherArtsButton" @click.stop="showArts()">
          <template #before>
            <SvgIcon name="arrow/chevron-left" width="20" height="20" />
          </template>
          {{ $t("programBoard.otherArts") }}
        </MenuListItem>

        <!-- 'Clear' button ("No Team") -->
        <MenuListItem v-if="showClear && selected" @click.stop="selectTeam(null)">
          <template #before>
            <SvgIcon name="persona/no-user" width="20" height="20" />
          </template>
          {{ $t("general.unassigned") }}
        </MenuListItem>

        <!-- Teams (currently-selected team is active+static)-->
        <MenuListItem
          v-for="team in teamsList"
          :key="team.id"
          :selected="team.id === selected?.id"
          :static="team.id === selected?.id"
          @click.stop="selectTeam(team)"
        >
          <template #before>
            <SvgIcon :name="teamIcon(team)" width="20" height="20" />
          </template>
          {{ team.name }}
        </MenuListItem>

        <MenuListItem v-if="!teamsList?.length" static>
          {{ $t("searchResults.noResults") }}
        </MenuListItem>
      </template>

      <!-- List of ARTs -->
      <template v-else>
        <MenuListItem
          v-for="art in artsList"
          :key="art.id"
          ref="artListItems"
          @click.stop="selectArt(art)"
        >
          <template #before>
            <SvgIcon name="general/art" width="20" height="20" />
          </template>
          {{ art.name }}
          <template #after>
            <SvgIcon name="arrow/chevron-right" width="20" height="20" />
          </template>
        </MenuListItem>

        <MenuListItem v-if="!artsList?.length" static>
          {{ $t("searchResults.noResults") }}
        </MenuListItem>
      </template>
    </MenuListItems>
  </MenuList>
</template>
